import { useCallback } from 'react'
import api from '../react-query/config/axios-config'
import { store } from '../redux-store'
import { setDownloadStatus } from '../containers/ImportStatus/actions'
import { exportStatuses } from '../containers/PlanViewerNew/constants'
import { useExportPlanStatusStore } from '../zustand/export-plan/use-export-plan-status-store'

const POLL_INTERVAL_MS = 1000

const activeExportStatuses = [exportStatuses.PENDING, exportStatuses.PROCESSING]

const fetchExportPlanStatus = async (jobId) => {
  const { data } = await api(`/api/plans-api/v3/printJobs/${jobId}/status`, {
    method: 'get',
    headers: { 'Cache-Control': 'no-cache' }
  })
  return data
}

// Shapes a print job response into what ImportStatus/reducer.js stores under
// `downloadStatus` and ImportStatus/index.js renders (see makeSelectDownloadStatus).
const buildDownloadStatus = (job, planName, fileName) => {
  const status = { planName, fileName, progress: 50 }

  if (job.status === exportStatuses.DONE) {
    status.downloadLink = job.links?.find(
      (link) => link.rel === 'download'
    )?.href
    status.progress = 100
  } else if (
    job.status === exportStatuses.FAILED ||
    job.status === exportStatuses.EXPIRED
  ) {
    status.error = job.error || true
  }

  return status
}

// Writes a job's status to both places that care about it: zustand (so any
// mounted component can reactively read live progress) and redux (what
// ImportStatus currently renders from).
const publishJobStatus = (job, planName, fileName) => {
  const status = buildDownloadStatus(job, planName, fileName)
  useExportPlanStatusStore.getState().setJobStatus(job.id, status)
  store.dispatch(setDownloadStatus(job.id, status))
  return status
}

// A plain (non-hook) polling loop — deliberately not a react-query
// `refetchInterval`, which pauses once nothing observes the query. A
// setTimeout loop keeps running regardless of which component (if any) is
// mounted, which is what lets an export's progress keep advancing after the
// export modal that started it navigates away.
const pollJobUntilFinished = (jobId, planName, fileName) => {
  setTimeout(async () => {
    let job
    try {
      job = await fetchExportPlanStatus(jobId)
    } catch {
      // transient failure (e.g. a dropped request) — try again on the next
      // tick instead of leaving the tray entry stuck
      pollJobUntilFinished(jobId, planName, fileName)
      return
    }

    publishJobStatus(job, planName, fileName)

    if (activeExportStatuses.includes(job.status)) {
      pollJobUntilFinished(jobId, planName, fileName)
    } else {
      useExportPlanStatusStore.getState().removeJob(jobId)
    }
  }, POLL_INTERVAL_MS)
}

// Call `trackExportPlanJob` once, right after a print job is created, to
// register it in the ImportStatus download tray and keep its progress
// updating until it's done, fails, or expires.
export const useExportPlanStatusTracker = () => {
  const trackExportPlanJob = useCallback((job, planName, fileName) => {
    if (useExportPlanStatusStore.getState().isTracked(job.id)) return

    publishJobStatus(job, planName, fileName)

    if (activeExportStatuses.includes(job.status)) {
      pollJobUntilFinished(job.id, planName, fileName)
    }
  }, [])

  return { trackExportPlanJob }
}
